import { Inject, inject, Injectable, Optional } from '@angular/core';
import { BehaviorSubject, combineLatest, Observable, of, switchMap } from 'rxjs';
import { RegionWithCities } from '../interfaces/regionality/IRegionWithCities';
import { CountryTax } from '../interfaces/regionality/ICountryTax';
import { HttpService } from './http.service';
import { AppRequests } from '../../const';
import { take } from 'rxjs/operators';
import { Currency } from '../interfaces/regionality/ICurrency';
import { CookieStorageService } from './cookie-storage.service';
import { City } from '../interfaces/regionality/ICity';
import { Country } from '../interfaces/regionality/ICountry';
import {
  RegionalityCookieState,
  RegionalityState,
} from '../interfaces/regionality/IRegionalityState';
import { DadataService } from './dadata.service';
import { REQUEST } from '../tokens/express.tokens';
import { TreeNode } from 'primeng/api';

@Injectable({
  providedIn: 'root',
})
export class RegionalityService {
  _http = inject(HttpService);
  _cookie = inject(CookieStorageService);
  _dadata = inject(DadataService);

  private countries = new BehaviorSubject<Country[] | null>(null);

  private regionsWithCities = new BehaviorSubject<RegionWithCities[] | null>(null);
  regionsWithCities$ = this.regionsWithCities.asObservable();

  private regionalityState = new BehaviorSubject<RegionalityState | null>(null);
  regionalityState$ = this.regionalityState.asObservable();

  constructor(@Optional() @Inject(REQUEST) private _request: any) {}

  initialize() {
    const cookieCurrentCountry = Number(this._cookie.get('ra-regionality-country'));
    const cookieCurrentCity = Number(this._cookie.get('ra-regionality-city'));
    if (!isNaN(cookieCurrentCountry) && !isNaN(cookieCurrentCity)) {
      const regionalityCookieState: RegionalityCookieState = {
        country: cookieCurrentCountry,
        city: cookieCurrentCity,
      };
      return this.getCountries().pipe(
        take(1),
        switchMap((countries) => {
          this.countries.next(countries);
          const findCountry = countries.find(
            (country) => country.id === regionalityCookieState.country
          );
          return combineLatest([
            this.getRegionsWithCities(findCountry ? findCountry.code : 'ru'),
            this.getCountryTax(findCountry ? findCountry.code : 'ru'),
            this.getCountryCurrencies(findCountry ? findCountry.code : 'ru'),
            of(findCountry ? findCountry : countries[0]),
          ]);
        }),
        switchMap(([regionsWithCities, countryTaxes, countryCurrencies, country]) => {
          const cities = regionsWithCities.map((region) => region.cities).flat();
          const city = cities.find((city) => city.id === regionalityCookieState.city);
          if (!city) {
            return this.getCurrentRegionalityByIP();
          }
          this.regionsWithCities.next(regionsWithCities);
          const newState: RegionalityState = {
            country: country,
            city: city,
            taxes: countryTaxes,
            currencies: Array.isArray(countryCurrencies) ? countryCurrencies : [countryCurrencies],
          };
          this.regionalityState.next(newState);
          return of(newState);
        })
      );
    } else {
      return this.getCurrentRegionalityByIP();
    }
  }

  getCurrentRegionalityByIP() {
    return this._dadata.getIpData(this.getUserIP()).pipe(
      take(1),
      switchMap((ipData) =>
        combineLatest([
          this.getRegionsWithCities(ipData ? ipData.country_iso_code.toLowerCase() : 'ru'),
          this.getCountryTax(ipData ? ipData.country_iso_code.toLowerCase() : 'ru'),
          this.getCountryCurrencies(ipData ? ipData.country_iso_code.toLowerCase() : 'ru'),
          this.getCountries(),
        ]).pipe(
          take(1),
          switchMap(([regionsWithCities, taxes, currencies, countries]) => {
            const city = ipData
              ? this.findCity(ipData.city, regionsWithCities)
              : regionsWithCities[0].cities[0];
            if (!city) {
              throw new Error('City not found based on IP data');
            }
            const country = ipData
              ? this.findCountry(ipData.country_iso_code, countries)
              : countries[0];
            if (!country) {
              throw new Error('Country not found based on IP data');
            }
            const newState: RegionalityState = {
              country: country,
              city: city,
              taxes: taxes,
              currencies: Array.isArray(currencies) ? currencies : [currencies],
            };
            this.regionalityState.next(newState);
            this._cookie.set('ra-regionality-country', newState.country.id.toString());
            this._cookie.set('ra-regionality-city', newState.city.id.toString());
            this.countries.next(countries);
            this.regionsWithCities.next(regionsWithCities);
            return of(newState);
          })
        )
      )
    );
  }

  getDownloadedRegionsWithCities() {
    return this.regionsWithCities.getValue();
  }

  private findCity(cityName: string, regionsWithCities: RegionWithCities[]): City | null {
    for (const region of regionsWithCities) {
      const foundCity = region.cities.find(
        (city) => city.name.toLowerCase() === cityName.toLowerCase()
      );
      if (foundCity) {
        return foundCity;
      }
    }
    return null; // Return null if no city matches
  }



  private findCountry(countryCode: string, countries: Country[]): Country | null {
    for (const country of countries) {
      if (country.code.toLowerCase() === countryCode.toLowerCase()) {
        return country;
      }
    }
    return null; // Return null if no country matches
  }

  getUserIP() {
    const ip: string = this._request?.get('x-forwarded-for') || '';
    const regex = /:\d+$/;
    return ip.replace(regex, '');
  }

  getCountries(): Observable<Country[]> {
    if (this.countries.value !== null) {
      return of(this.countries.value); // Используем оператор 'of' для создания Observable из значения
    } else {
      return this._http
        .request(AppRequests['getCountries']) // Предполагаем, что запрос возвращает массив стран
        .pipe(take(1));
    }
  }

  getRegionsWithCities(countryCode: string): Observable<RegionWithCities[]> {
    return this._http.request(AppRequests['getRegionsWithCities'], null, countryCode).pipe(take(1));
  }

  getCountryTax(countryCode: string): Observable<CountryTax[]> {
    return this._http.request(AppRequests['getCountryTaxTypes'], null, countryCode).pipe(take(1));
  }

  getCountryCurrencies(countryCode: string): Observable<Currency[]> {
    return this._http.request(AppRequests['getCountryCurrencies'], null, countryCode).pipe(take(1));
  }

  generateNewRegionality(city: City, countryCode: string) {
    const country = this.findCountry(countryCode, this.countries.value || []);
    if (!country) {
      throw new Error('Country not found based on IP data');
    }
    return combineLatest([
      this.getCountryCurrencies(countryCode),
      this.getCountryTax(countryCode),
    ]).pipe(
      switchMap(([currencies, taxes]) => {
        const newState: RegionalityState = {
          country: country,
          city: city,
          taxes: taxes,
          currencies: Array.isArray(currencies) ? currencies : [currencies],
        };
        return of(newState);
      })
    );
  }

  setNewRegionality(state: RegionalityState) {
    this._cookie.set('ra-regionality-country', state.country.id.toString());
    this._cookie.set('ra-regionality-city', state.city.id.toString());
    this.regionalityState.next(state);
  }

  getCurrentRegionality$(): Observable<RegionalityState> {
    return this.regionalityState$ as Observable<RegionalityState>;
  }

  getCurrentRegionality() {
    return this.regionalityState.value;
  }

  generateTreeFormRegionsWithCities(arr: RegionWithCities[] | null): TreeNode[] {
    if (!arr) {
      return [];
    }

    return arr.map((region) => ({
      label: region.name,
      data: region.id,
      children: region.cities.map((city) => ({
        label: city.name,
        data: city.id,
      })),
    }));
  }
}
